import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { ShoppingBag, X, ExternalLink } from 'lucide-react';
import { RyanConfig } from '../types/ryan.types';
import './RyanCompraModal.css';

interface RyanCompraModalProps {
  isOpen: boolean;
  onClose: () => void;
  spaceId?: string;
  spaceName: string;
  ryanConfig?: RyanConfig;
  // Dados já resolvidos — usado fora da área logada (fim da prévia pública de
  // apostila), onde não dá pra chamar /spaces/:spaceId/product-link (exige
  // acesso Ryan). Quando presente, não busca nada.
  preset?: {
    purchaseUrl: string;
    productTitle: string;
    publicSlug: string | null;
    whatsappNumber: string;
  };
  title?: string;
  message?: React.ReactNode;
}

interface ProductLink {
  title: string;
  publicSlug: string;
  whatsappNumber: string;
}

function buildWhatsappUrl(number: string, productTitle: string): string {
  const cleaned = number.replace(/\D/g, '');
  const full = cleaned.startsWith('55') ? cleaned : `55${cleaned}`;
  const msg = `Oii, quero adquirir o ${productTitle}!`;
  return `https://wa.me/${full}?text=${encodeURIComponent(msg)}`;
}

const RyanCompraModal: React.FC<RyanCompraModalProps> = ({
  isOpen, onClose, spaceId, spaceName, ryanConfig, preset, title, message,
}) => {
  const [product, setProduct] = useState<ProductLink | null>(null);
  // Link configurado no campo (Layout da Página) — tem prioridade sobre o produto automático
  const [purchaseUrl, setPurchaseUrl] = useState('');
  // Enquanto true, a área de botões mostra um placeholder em vez do conteúdo
  // final — evita o "Ver produto" aparecer de repente (pop-in) depois que o
  // resto do modal já está visível há um tempinho, esperando o fetch de
  // /spaces/:spaceId/product-link resolver.
  const [loadingLink, setLoadingLink] = useState(true);

  useEffect(() => {
    if (!isOpen) return;
    if (preset) {
      setProduct(preset.publicSlug
        ? { title: preset.productTitle, publicSlug: preset.publicSlug, whatsappNumber: preset.whatsappNumber }
        : null);
      setPurchaseUrl(preset.purchaseUrl || '');
      setLoadingLink(false);
      return;
    }
    if (!spaceId) return;
    setProduct(null);
    setPurchaseUrl('');
    setLoadingLink(true);
    const token = localStorage.getItem('accessToken');
    fetch(`/api/ryan/spaces/${spaceId}/product-link`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        setProduct(data?.product || null);
        setPurchaseUrl(data?.purchaseUrl || '');
      })
      .catch(() => setProduct(null))
      .finally(() => setLoadingLink(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, spaceId, preset?.purchaseUrl, preset?.publicSlug]);

  if (!isOpen) return null;

  const displayName = product?.title || preset?.productTitle || spaceName;
  const whatsappNumber = product?.whatsappNumber || preset?.whatsappNumber || ryanConfig?.whatsappNumber || '';
  const showVerProduto = !purchaseUrl && !!product?.publicSlug;

  const handleWhatsApp = () => {
    window.open(buildWhatsappUrl(whatsappNumber, displayName), '_blank');
    onClose();
  };

  return ReactDOM.createPortal(
    <div className="rcm-overlay" onClick={onClose}>
      <div className="rcm-modal" onClick={e => e.stopPropagation()}>
        <button className="rcm-close-btn" onClick={onClose}>
          <X size={18} />
        </button>

        <div className="rcm-icon-wrap">
          <ShoppingBag size={32} />
        </div>

        <h2 className="rcm-title">{title || 'Quase lá!'}</h2>
        <p className="rcm-text">
          {message || (
            <>
              Esse conteúdo faz parte de <strong>"{displayName}"</strong> e você ainda não tem acesso a ele.
              Mas dá pra resolver rapidinho.
            </>
          )}
        </p>

        <div className="rcm-actions">
          {loadingLink ? (
            <>
              <div className="rcm-btn-skeleton" />
              <div className="rcm-btn-skeleton" />
            </>
          ) : (
            <>
              {purchaseUrl && (
                <a
                  className="rcm-btn-primary"
                  href={purchaseUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onClose}
                >
                  <ExternalLink size={15} />
                  Quero adquirir
                </a>
              )}
              {showVerProduto && (
                <a
                  className="rcm-btn-primary"
                  href={`/ryan/produto/${product!.publicSlug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onClose}
                >
                  <ExternalLink size={15} />
                  Ver produto
                </a>
              )}
              {whatsappNumber && (
                <button className={purchaseUrl ? 'rcm-btn-secondary' : 'rcm-btn-primary'} onClick={handleWhatsApp}>
                  {purchaseUrl ? 'Tirar dúvidas no WhatsApp' : 'Falar no WhatsApp'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RyanCompraModal;
